import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import SingleBet from 'modules/betslip/betslip-view/single-bet/index.js';
import { useSelector } from 'react-redux';
import { pullAll } from 'mojito/utils';

const classUtils = MojitoCore.Base.classUtils;
const { selectCurrency } = MojitoServices.UserInfo.selectors;
const {
    utils: BetslipUtils,
    selectors: { selectBetsState },
    types: BetslipTypes,
} = MojitoServices.Betslip;
const { FlexPane, FlexPaneSeparator } = MojitoPresentation.Components;

const shouldHighlightRelatedBet = bet => bet.hasRelatedBet && !BetslipUtils.isBetBuilderBet(bet);

const sanitizeSingleBetsList = (bets, betslip) => {
    const matchAccaBets = BetslipUtils.getMatchAccaBets(betslip);
    const matchAccaSelectionIds = matchAccaBets.flatMap(BetslipUtils.getSelectionIdsFromBet);
    const singlesInMatchAccas = bets.filter(bet => {
        const isOneLegBet = BetslipUtils.isDefault(bet) && bet.parts.length === 1;
        if (isOneLegBet) {
            const { selectionId } = BetslipUtils.getFirstBetPart(bet);
            return matchAccaSelectionIds.includes(selectionId);
        }
    });
    pullAll(bets, singlesInMatchAccas);
};

export default function SingleBetsList(props) {
    const { DEFAULT, SINGLES, MULTIPLES, SYSTEMS } = BetslipTypes.STAKE_GROUP_NAME;
    const {
        mojitoTools,
        activeStakeGroup,
        betslip,
        betslipStatus,
        forceEWLabelsToShow,
        highlightEnabled,
        hasPendingBonus,
        bankersEnabled,
        stakingEnabled,
        class: className,
    } = props;
    const { config } = mojitoTools;
    const betsState = useSelector(state => selectBetsState(state));
    const currency = useSelector(state => selectCurrency(state));
    const isSingles = activeStakeGroup === SINGLES;
    const isDefault = activeStakeGroup === DEFAULT;
    const isMultiples = activeStakeGroup === MULTIPLES;
    const isSystems = activeStakeGroup === SYSTEMS;

    const showBanker = bankersEnabled && BetslipUtils.isBanker(activeStakeGroup);
    const bets = BetslipUtils.getSingleBets(betslip);
    const filteredBets = (isMultiples || isSystems) && sanitizeSingleBetsList(bets, betslip);

    const renderSeparator = (key, idx) =>
        BetslipUtils.isBetBuilderBet(bets[idx]) ? null : (
            <FlexPaneSeparator config={config.separator} key={key} direction={'column'} />
        );

    const renderSingleBets = () =>
        (filteredBets || bets).map(bet => {
            const betState = betsState[bet.id] || {};
            const {
                isSuspended,
                isExpired,
                suspendedParts,
                hasOddsChange,
                hasHcapChange,
                isInvalid,
                isBankerPending,
            } = betState;

            const highlight =
                ((isSingles || isDefault) && !BetslipUtils.hasAvailableBetWays(bet)) ||
                (highlightEnabled &&
                    (isInvalid || shouldHighlightRelatedBet(bet) || bet.notCombinable));

            const isEachWayAvailableForBet = BetslipUtils.isEachWayAvailableForBet(bet);
            const showStake =
                stakingEnabled && (isSingles || isDefault) && BetslipUtils.hasAvailableBetWays(bet);

            const eachWayAvailable = (isSingles || isDefault) && isEachWayAvailableForBet;
            const betStake = BetslipUtils.getBetStake(betslip, bet.id, activeStakeGroup);
            return (
                <SingleBet
                    betslip={betslip}
                    config={config.singleBet}
                    suspendedParts={suspendedParts}
                    key={`single-bet-${bet.id}`}
                    bet={bet}
                    showStake={showStake}
                    highlight={highlight}
                    betStake={betStake}
                    suspended={isSuspended}
                    expired={isExpired}
                    hasOddsChange={!BetslipUtils.isTeaser(activeStakeGroup) && hasOddsChange}
                    hasHcapChange={hasHcapChange}
                    showEachWay={eachWayAvailable}
                    betslipStatus={betslipStatus}
                    currencyCode={currency}
                    forceEWLabelsToShow={isEachWayAvailableForBet && forceEWLabelsToShow}
                    showBanker={showBanker}
                    isBankerPending={isBankerPending}
                    isTeaser={BetslipUtils.isTeaser(activeStakeGroup)}
                    showPotentialReturns={config.showPotentialReturns}
                    hasPendingBonus={hasPendingBonus}
                />
            );
        });

    return (
        <FlexPane
            class={classUtils.classNames('ta-SingleBetsList', className)}
            config={config.container}
            separator={renderSeparator}
        >
            {renderSingleBets()}
        </FlexPane>
    );
}
